import React from 'react'
import { useForm } from 'react-hook-form'
import { Grid, Button, makeStyles } from '@material-ui/core'
import { Form, TextField, SelectField, AddressLookup, PhoneField } from '../common/form'
import { yupResolver } from '@hookform/resolvers'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { clearGatewayError, RootState, setConsumerFormPayload } from 'src/lib/store'
import { ConsumerInfo } from 'src/lib/types'
import { STATES, DELIVERY_METHODS } from 'src/lib/constants'
import { DatePickerField } from '../common/form/fields/DatePickerField'
import { format } from 'date-fns'
import { genConsumerValidationSchema, genConsumerDefaultValues } from 'src/lib/helpers'

export const ConsumerInfoForm: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const payloadState = useSelector((state: RootState) => state.payload.data)
  const validationSchema = genConsumerValidationSchema(payloadState.onBehalf)

  const { control, errors, setValue, watch, handleSubmit } = useForm<ConsumerInfo>({
    resolver: yupResolver(validationSchema),
    defaultValues: genConsumerDefaultValues(payloadState)
  })

  const watchFields = watch()

  const goBack = () => {
    if (watchFields.dob !== '') dispatch(setConsumerFormPayload({ ...watchFields, dob: format(new Date(watchFields.dob), 'MM/dd/yyyy') }))
    if (watchFields.dob === '') dispatch(setConsumerFormPayload({ ...watchFields, dob: '' }))
    history.goBack()
  }

  const onSubmit = (payload: ConsumerInfo) => {
    /* make sure that gateway error is cleared in case user clicked the back button to correct error */
    dispatch(clearGatewayError())
    dispatch(setConsumerFormPayload({ ...payload, deliveryMethod: payloadState.onBehalf === 'No' ? payload.deliveryMethod : payloadState.deliveryMethod, dob: format(new Date(payload.dob), 'MM/dd/yyyy') }))
    history.push('/confirm')
  }

  return (
    <Form title={`About ${payloadState.onBehalf === 'Yes' ? '' : 'YOU as'} the Consumer`} handleSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        {payloadState.onBehalf === 'No' && (
          <>
            <Grid item xs={12} sm={6}>
              <SelectField control={control} name="deliveryMethod" testId="deliveryMethod" defaultValue={''} label="Communication Preference" items={DELIVERY_METHODS} error={errors.deliveryMethod} />
            </Grid>
            <Grid item sm={6} />
          </>
        )}
        <Grid item xs={12} sm={4}>
          <TextField name="firstName" label="First Name" control={control} error={errors.firstName} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField name="middleName" label="Middle Name" control={control} error={errors.middleName} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField name="lastName" label="Last Name" control={control} error={errors.lastName} />
        </Grid>
        <Grid item xs={12} sm={1}>
          <TextField name="suffix" label="Suffix" control={control} error={errors.suffix} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="email" label="Email" control={control} error={errors.email} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <PhoneField name="phone" label="Phone Number" control={control} setField={setValue} fields={watchFields} error={errors.phone} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="accountNumber" label="Account Number" type='number' control={control} error={errors.accountNumber} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePickerField control={control} label="Date of Birth" error={errors.dob} />
        </Grid>
        <Grid item xs={12}>
          <AddressLookup name="addressLine1" control={control} fields={watchFields} label="Mailing Address Line 1" setField={setValue} error={errors.addressLine1} />
        </Grid>
        <Grid item xs={12}>
          <TextField name="addressLine2" label="Address Line 2 (optional)" control={control} error={errors.addressLine2} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="city" label="City" control={control} error={errors.city} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SelectField control={control} name="state" testId="states" defaultValue={''} label="State" items={STATES} error={errors.state} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField name="zip" type="phone" label="ZIP Code" inputProps={{ maxLength: 10 }} control={control} error={errors.zip} />
        </Grid>
        <Grid item xs={6}>
          <Button className={classes.button} color="primary" variant="contained" onClick={() => goBack()}>
            Back
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button className={classes.button} color="primary" variant="contained" type="submit">
            Next
          </Button>
        </Grid>
      </Grid>
    </Form>
  )
}

const useStyles = makeStyles({
  buttonContainer: { display: 'flex', width: '100%', marginTop: 25 },
  button: { marginTop: 25, width: '100%' }
})
